/**
 * Strategy Completeness Mock
 *
 * Side-by-side comparison of a "complete" strategy (all five
 * components defined) vs an "incomplete" one (only entry defined,
 * everything else vague). Visual audit checklist.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

interface StrategyRow {
  component: string;
  complete: string;
  incomplete: string;
}

const rows: StrategyRow[] = [
  {
    component: 'Entry',
    complete: 'RSI(14) crosses above 30 while price > EMA(200)',
    incomplete: 'RSI oversold with trend confirmation',
  },
  {
    component: 'Exit',
    complete: 'Trailing stop at 2x ATR(14) or target at 3:1 R',
    incomplete: 'Take profit when it looks right',
  },
  {
    component: 'Risk Mgmt',
    complete: 'Max 1.5% equity per trade, 15% max drawdown halt',
    incomplete: 'Not defined',
  },
  {
    component: 'Filters',
    complete: 'Only trade when ADX(14) > 20 and not within 4h of news',
    incomplete: 'Not defined',
  },
  {
    component: 'Sizing',
    complete: 'Risk-based: 1.5% equity / (entry - stop) = position size',
    incomplete: 'Put in a decent amount',
  },
];

function Panel({
  title, color, pass, children,
}: {
  title: string; color: string; pass: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mock-compare-panel" style={{ flex: 1 }}>
      <div className="mock-compare-title" style={{ color }}>
        {title}
      </div>
      {children}
      <div style={{
        marginTop: '0.5rem', padding: '0.35rem 0.5rem', borderRadius: 5,
        background: pass ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
        border: `1px solid ${pass ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
        fontSize: '0.625rem', fontWeight: 600,
        color: pass ? '#22c55e' : '#ef4444', textAlign: 'center',
      }}>
        {pass ? 'Backtestable — ready to validate' : 'Not testable — gaps in logic'}
      </div>
    </div>
  );
}

export default function StrategyCompletenessMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Strategy Completeness Audit
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        <div className="mock-compare" style={{ alignItems: 'stretch' }}>
          {/* Complete strategy */}
          <Panel title="Complete Strategy" color="#22c55e" pass>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {rows.map((r) => (
                <div key={r.component} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
                  padding: '0.3rem 0.4rem', background: '#181825',
                  borderRadius: 4, border: '1px solid rgba(34,197,94,0.1)',
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: '0.05rem',
                  }}>
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.45rem', color: '#22c55e' }} />
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.625rem', fontWeight: 700, color: '#e5e7eb',
                    }}>{r.component}</div>
                    <div style={{
                      fontSize: '0.5625rem', color: '#9ca3af',
                    }}>{r.complete}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Incomplete strategy */}
          <Panel title="Incomplete Strategy" color="#ef4444" pass={false}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {rows.map((r, i) => {
                const defined = i === 0; // Only entry is properly defined
                return (
                  <div key={r.component} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
                    padding: '0.3rem 0.4rem', background: '#181825',
                    borderRadius: 4,
                    border: `1px solid ${defined ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.1)'}`,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                      background: defined ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginTop: '0.05rem',
                    }}>
                      {defined ? (
                        <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.45rem', color: '#f59e0b' }} />
                      ) : (
                        <FontAwesomeIcon icon={faXmark} style={{ fontSize: '0.45rem', color: '#ef4444' }} />
                      )}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '0.625rem', fontWeight: 700, color: '#e5e7eb',
                      }}>{r.component}</div>
                      <div style={{
                        fontSize: '0.5625rem',
                        color: defined ? '#9ca3af' : '#6b7280',
                        fontStyle: defined ? 'normal' : 'italic',
                      }}>{r.incomplete}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#ef4444', fontWeight: 600 }}>The gap: </span>
          Both traders say they have a "strategy." Only one can actually test it.
          Vague rules cannot be backtested — they produce different results every time.
        </div>
      </div>
      <div className="platform-mock-caption">
        A strategy without defined exits, risk, filters, and sizing is not a strategy — it is a guess.
      </div>
    </div>
  );
}
